import React, {useEffect, useRef, useState} from 'react';
import * as THREE from 'three';
import {GLTFLoader} from 'three/examples/jsm/loaders/GLTFLoader';
import {useSelector} from "react-redux";
import {selectVisibleMaterials} from "../../features/materials/materialsSlice";
import {OrbitControls} from "three/examples/jsm/controls/OrbitControls";

/**
 * ThreeJSModelViewer Component
 *
 * Dynamically applies colors to:
 *   - Case objects  (DWG_72IMOSXD01_IM_SHELF_FIX, DWG_72IMOSXD01_IM_SHELF_INT,
 *                    DWG_72IMOSXD01_IM_SIDE_INT, DWG_72IMOSXD01_IM_SIDE_NORM)
 *   - Front objects (DWG_72IMOSXD01_IM_DOOR_NORM, DWG_72IMOSXD01_IM_DRAWER_FRONT,
 *                    DWG_72IMOSXD01_IM_ENDPANEL)
 *   - Back objects  (DWG_72IMOSXD01_IM_BACK)
 *
 * Additionally accepts:
 *   - viewFronts: if true, makes door/drawer front objects visible (unless excludeFronts is set).
 *   - resetView: if true, resets the camera to the original vantage point.
 *
 * If cabinet.excludeFronts is true (boolean or string "true"), front objects are hidden.
 *
 * @param {Object} props
 * @param {Object} props.cabinet - The cabinet object containing:
 *   { caseMaterial, frontMaterial, backPanelMaterial, excludeFronts, serialNumber,
 *     width, height, depth, etc. }
 * @param {boolean} props.viewFronts - Toggle visibility for front objects (door & drawer).
 * @param {boolean} props.resetView - If true, re-centers/re-zooms the camera on the model.
 * @param {boolean} props.excludeFronts - If true, override front visibility to hidden.
 */
const ThreeJSModelViewer = ({cabinet, viewFronts, resetView, excludeFronts}) => {
    const mountRef = useRef(null);

    const sceneRef = useRef(null);
    const modelRef = useRef(null);
    const originalModelRef = useRef(null);

    const controlsRef = useRef(null);
    const cameraRef = useRef(null);
    const rendererRef = useRef(null);

    // Refs for lights to update their positions
    const bottomSpotlightRef = useRef(null);
    const bottomDirectionalRef = useRef(null);

    // Store the current viewFronts in a ref (so it persists during model reloads).
    const currentViewFrontsRef = useRef(viewFronts);

    // Track when model is fully loaded and ready for updates
    const [modelLoaded, setModelLoaded] = useState(false);

    // All materials from Redux store.
    const materials = useSelector(selectVisibleMaterials);

    // Model file path
    const modelUrlRef = useRef(null);

    // Conversion factor: inches to meters for ThreeJS
    const INCHES_TO_METERS = 0.0254;

    // Arrays for each category of parts.
    const frontNames = [
        "72IMOSXD01_IM_DOOR_NORM",
        "72IMOSXD01_IM_DRAWER_FRONT",
        "72IMOSXD01_IM_ENDPANEL"
    ];
    const backNames = ["72IMOSXD01_IM_BACK"];
    const caseNames = [
        "72IMOSXD01_IM_SHELF_FIX",
        "72IMOSXD01_IM_SHELF_INT",
        "72IMOSXD01_IM_SIDE_INT",
        "72IMOSXD01_IM_SIDE_NORM",
    ];

    // Ref for debouncing dimension updates
    const updateDimensionsTimerRef = useRef(null);

    //----------------------------------------------------------------------
    // 1. Hooks: Setting up local states / watchers
    //----------------------------------------------------------------------

    // On mount or changes to cabinet fields, set model URL
    useEffect(() => {
        if (cabinet && cabinet.serialNumber) {
            modelUrlRef.current = `https://www.thesealab.com/models/${cabinet.serialNumber}.glb`;
        } else {
            // Fallback: default model
            modelUrlRef.current = `/models/test1.glb`;
        }
    }, [
        cabinet.serialNumber,
        cabinet.caseMaterial,
        cabinet.frontMaterial,
        cabinet.backPanelMaterial
    ]);

    // Keep viewFronts in sync
    useEffect(() => {
        currentViewFrontsRef.current = viewFronts;
    }, [viewFronts]);

    //----------------------------------------------------------------------
    // 2. Color mapping and applying
    //----------------------------------------------------------------------

    /**
     * Get color by material description instead of texture
     */
    const getColorByDescription = (desc) => {
        if (!desc) return Promise.resolve(null);
        const found = materials.find((m) => m.description === desc);
        if (!found) {
            console.warn(`No matching material found for description "${desc}".`);
            return Promise.resolve(null);
        }

        const description = found.description.toLowerCase();
        let colorHex;

        if (description.includes('maple') || description.includes('birch'))
            colorHex = 0xD4B483; // Light wood color
        else if (description.includes('medex') || description.includes('plumaply'))
            colorHex = 0xC0B090; // MDF color
        else if (description.includes('formica') || description.includes('melamine'))
            colorHex = 0xFFFFFF; // White
        else if (description.includes('oak'))
            colorHex = 0xAA8866; // Oak color
        else
            colorHex = 0xCCCCCC; // Default light gray

        return Promise.resolve(colorHex);
    };

    /**
     * Applies a given color to all meshes within an object's hierarchy.
     */
    const applyColorToObject = (object3D, color) => {
        object3D.traverse((child) => {
            if (child.isMesh) {
                child.material = new THREE.MeshStandardMaterial({
                    color: color || 0xCCCCCC, // Default to light gray if no color provided
                    metalness: 0.2,
                    roughness: 0.8,
                    envMapIntensity: 1.0,
                    side: THREE.DoubleSide
                });
                child.castShadow = false;
                child.receiveShadow = false;
            }
        });
    };

    /**
     * For each name in namesArray, apply the given color if the object is found.
     */
    const applyColorByNameList = async (model, namesArray, color) => {
        namesArray.forEach((objectName) => {
            const obj = model.getObjectByName(objectName);
            if (obj) {
                applyColorToObject(obj, color);
            }
        });
    };

    /**
     * Main method to apply all relevant colors (case, front, back) to the model.
     */
    const applyColorsToModel = async (model, cabinetData) => {
        try {
            const promises = [];
            if (cabinetData.caseMaterial) {
                promises.push(
                    getColorByDescription(cabinetData.caseMaterial).then((caseColor) => {
                        if (caseColor) {
                            return applyColorByNameList(model, caseNames, caseColor);
                        }
                    })
                );
            }
            if (cabinetData.frontMaterial) {
                promises.push(
                    getColorByDescription(cabinetData.frontMaterial).then((frontColor) => {
                        if (frontColor) {
                            return applyColorByNameList(model, frontNames, frontColor);
                        }
                    })
                );
            }
            if (cabinetData.backPanelMaterial) {
                promises.push(
                    getColorByDescription(cabinetData.backPanelMaterial).then((backColor) => {
                        if (backColor) {
                            return applyColorByNameList(model, backNames, backColor);
                        }
                    })
                );
            }
            await Promise.all(promises);
        } catch (error) {
            console.error("Error applying colors:", error);
        }
    };

    //----------------------------------------------------------------------
    // 3. Camera & scene adjustments
    //----------------------------------------------------------------------

    /**
     * Adjust the perspective camera so the model is centered and framed nicely.
     */
    const adjustCameraToFitModel = (model) => {
        if (!model || !cameraRef.current || !controlsRef.current) return;
        const bbox = new THREE.Box3().setFromObject(model);
        const center = bbox.getCenter(new THREE.Vector3());
        const size = bbox.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        const fov = cameraRef.current.fov * (Math.PI / 180);
        let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));
        cameraZ *= 1.4;
        const offset = new THREE.Vector3(maxDim * 0.3, maxDim * 0.2, cameraZ);
        cameraRef.current.position.set(center.x + offset.x, center.y + offset.y, center.z + offset.z);
        cameraRef.current.lookAt(center);
        cameraRef.current.near = 0.01;
        cameraRef.current.far = cameraZ * 10;
        cameraRef.current.updateProjectionMatrix();
        controlsRef.current.target.copy(center);
        controlsRef.current.update();
    };

    /**
     * Update front object (door/drawer) visibility based on `viewFronts` and `excludeFronts`.
     */
    const updateFrontVisibility = () => {
        if (!modelRef.current) return;
        const doorObj = modelRef.current.getObjectByName("72IMOSXD01_IM_DOOR_NORM");
        const drawerObj = modelRef.current.getObjectByName("72IMOSXD01_IM_DRAWER_FRONT");
        const endPanelObj = modelRef.current.getObjectByName("72IMOSXD01_IM_ENDPANEL");
        const isExcluded =
            cabinet &&
            (cabinet.excludeFronts === true || cabinet.excludeFronts === "true" || excludeFronts);
        if (isExcluded) {
            if (doorObj) doorObj.visible = false;
            if (drawerObj) drawerObj.visible = false;
            if (endPanelObj) endPanelObj.visible = true;
        } else {
            if (doorObj) doorObj.visible = currentViewFrontsRef.current;
            if (drawerObj) drawerObj.visible = currentViewFrontsRef.current;
            if (endPanelObj) endPanelObj.visible = true;
        }
    };

    //----------------------------------------------------------------------
    // 4. Model dimension updates whenever user changes cabinet.width/height/depth
    //----------------------------------------------------------------------

    useEffect(() => {
        if (!sceneRef.current || !originalModelRef.current) return;
        if (updateDimensionsTimerRef.current) clearTimeout(updateDimensionsTimerRef.current);
        updateDimensionsTimerRef.current = setTimeout(async () => {
            const updateModelDimensions = async () => {
                const width = parseFloat(cabinet.width) || 30;
                const height = parseFloat(cabinet.height) || 30;
                const depth = parseFloat(cabinet.depth) || 24;
                const widthMeters = width * INCHES_TO_METERS;
                const heightMeters = height * INCHES_TO_METERS;
                const depthMeters = depth * INCHES_TO_METERS;
                if (modelRef.current) {
                    sceneRef.current.remove(modelRef.current);
                }
                const newModel = originalModelRef.current.clone();
                const geometryContainer = newModel.getObjectByName("Geometry") || newModel;
                const box = new THREE.Box3().setFromObject(geometryContainer);
                const size = box.getSize(new THREE.Vector3());
                const scale = new THREE.Vector3(
                    widthMeters / size.x,
                    depthMeters / size.z,
                    heightMeters / size.y
                );
                geometryContainer.scale.copy(scale);
                const newBox = new THREE.Box3().setFromObject(geometryContainer);
                const center = newBox.getCenter(new THREE.Vector3());
                newModel.position.set(-center.x, -center.y, -center.z);
                modelRef.current = newModel;
                sceneRef.current.add(newModel);
                await applyColorsToModel(newModel, cabinet);
                updateFrontVisibility();
                adjustCameraToFitModel(newModel);
                setModelLoaded(true);
            };
            updateModelDimensions();
        }, 200);
        return () => clearTimeout(updateDimensionsTimerRef.current);
    }, [cabinet.width, cabinet.height, cabinet.depth]);

    //----------------------------------------------------------------------
    // 5. Main effect: initialize scene, camera, renderer, controls, load GLB
    //----------------------------------------------------------------------

    useEffect(() => {
        if (!mountRef.current || !modelUrlRef.current) return;
        setModelLoaded(false);
        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0xf8f9fa);
        const envMapLoader = new THREE.CubeTextureLoader();
        const envMap = envMapLoader.load([
            '/textures/env/px.jpg', '/textures/env/nx.jpg',
            '/textures/env/py.jpg', '/textures/env/ny.jpg',
            '/textures/env/pz.jpg', '/textures/env/nz.jpg'
        ]);
        scene.environment = envMap;
        sceneRef.current = scene;
        const camera = new THREE.PerspectiveCamera(
            45,
            mountRef.current.clientWidth / mountRef.current.clientHeight,
            0.01,
            1000
        );
        cameraRef.current = camera;
        const renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true,
            preserveDrawingBuffer: true
        });
        renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.setPixelRatio(window.devicePixelRatio);
        renderer.physicallyCorrectLights = true;
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.2;
        renderer.shadowMap.enabled = false;
        rendererRef.current = renderer;
        mountRef.current.appendChild(renderer.domElement);
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.2;
        controls.minDistance = 0.5;
        controls.maxDistance = 10;
        controls.enablePan = true;
        controls.screenSpacePanning = true;
        controlsRef.current = controls;

        // Add lights

        // Hemisphere light from above
        const hemiLight = new THREE.HemisphereLight(0xbbbbff, 0x444422, 1.5);
        hemiLight.position.set(0, 1, 0);
        scene.add(hemiLight);

        // Main directional light from top-right
        const dirLight = new THREE.DirectionalLight(0xffffff, 1.5);
        dirLight.position.set(5, 10, 5);
        dirLight.castShadow = false;
        scene.add(dirLight);

        // Back light
        const backLight = new THREE.DirectionalLight(0xffffff, 1.5);
        backLight.position.set(-5, 4, -5);
        scene.add(backLight);

        // Center point light
        const pointLight = new THREE.PointLight(0xffffee, 0.1);
        pointLight.position.set(0, 0, 0);
        scene.add(pointLight);


        // Add a target for the spotlight
        const spotlightTarget = new THREE.Object3D();
        spotlightTarget.position.set(0, 0, 0);
        scene.add(spotlightTarget);

        const bottomLight = new THREE.DirectionalLight(0xffffff, 0.5);
        bottomLight.position.set(0, -10, 0)
        scene.add(bottomLight);


        // Add a target for the directional light
        const dirLightTarget = new THREE.Object3D();
        dirLightTarget.position.set(0, 0, 0);
        scene.add(dirLightTarget);

        const loader = new GLTFLoader();
        loader.load(
            modelUrlRef.current,
            async (gltf) => {
                const model = gltf.scene;
                originalModelRef.current = model.clone();
                modelRef.current = model;
                if (cabinet) {
                    try {
                        await applyColorsToModel(model, cabinet);
                    } catch (error) {
                        console.error("Error applying colors:", error);
                    }
                }
                const geometryContainer = model.getObjectByName("Geometry") || model;
                const box = new THREE.Box3().setFromObject(geometryContainer);
                const center = box.getCenter(new THREE.Vector3());
                model.position.set(-center.x, -center.y, -center.z);
                pointLight.position.set(center.x, center.y, center.z);

                // Position bottom lights based on model size
                const size = box.getSize(new THREE.Vector3());
                const bottomY = center.y - (size.y / 2) - 0.3;

                spotlightTarget.position.set(center.x, center.y, center.z);
                dirLightTarget.position.set(center.x, center.y, center.z);

                if (cabinet.width && cabinet.height && cabinet.depth) {
                    const widthMeters = cabinet.width * INCHES_TO_METERS;
                    const heightMeters = cabinet.height * INCHES_TO_METERS;
                    const depthMeters = cabinet.depth * INCHES_TO_METERS;
                    const size = box.getSize(new THREE.Vector3());
                    geometryContainer.scale.set(
                        widthMeters / size.x,
                        depthMeters / size.z,
                        heightMeters / size.y
                    );
                }
                scene.add(model);
                updateFrontVisibility();
                adjustCameraToFitModel(model);
                setModelLoaded(true);
            },
            undefined,
            (error) => {
                console.error("Error loading model:", error);
            }
        );
        const animate = () => {
            requestAnimationFrame(animate);
            controls.update();
            renderer.render(scene, camera);
        };
        animate();
        const handleResize = () => {
            if (!mountRef.current) return;
            const newWidth = mountRef.current.clientWidth;
            const newHeight = mountRef.current.clientHeight;
            camera.aspect = newWidth / newHeight;
            camera.updateProjectionMatrix();
            renderer.setSize(newWidth, newHeight);
        };
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
            if (mountRef.current && renderer.domElement) {
                mountRef.current.removeChild(renderer.domElement);
            }
            scene.traverse((object) => {
                if (object.geometry) object.geometry.dispose();
                if (object.material) {
                    if (Array.isArray(object.material)) {
                        object.material.forEach((mat) => {
                            mat.dispose();
                        });
                    } else {
                        object.material.dispose();
                    }
                }
            });
            renderer.dispose();
        };
    }, [cabinet]);

    //----------------------------------------------------------------------
    // 6. Watch for front-visibility or excludeFronts changes after model loads
    //----------------------------------------------------------------------

    useEffect(() => {
        if (modelLoaded) {
            updateFrontVisibility();
        }
    }, [viewFronts, excludeFronts, cabinet.excludeFronts, modelLoaded]);

    //----------------------------------------------------------------------
    // 7. Handle resetView
    //----------------------------------------------------------------------

    useEffect(() => {
        if (resetView && modelRef.current) {
            adjustCameraToFitModel(modelRef.current);
        }
    }, [resetView]);

    //----------------------------------------------------------------------
    // 8. Watch for material changes
    //----------------------------------------------------------------------

    useEffect(() => {
        if (modelLoaded && modelRef.current) {
            applyColorsToModel(modelRef.current, cabinet);
        }
    }, [cabinet.caseMaterial, cabinet.frontMaterial, cabinet.backPanelMaterial, modelLoaded]);

    //----------------------------------------------------------------------
    // Render
    //----------------------------------------------------------------------

    return (
        <div
            ref={mountRef}
            style={{
                width: "100%",
                height: "350px",
                borderRadius: "8px",
                overflow: "hidden",
                boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
            }}
        />
    );
};

export default ThreeJSModelViewer;